/**
 * LDT Post-RP T Stage and Surgical Margin fields.
 * Supports T Stage "Not Provided" for CS/Sales.
 */
import { Controller, Path } from 'react-hook-form';
import { Box } from '@mui/material';
import z from 'zod';
import { RadioGroupField } from 'src/components/radio_group';
import {
  FormFieldProps,
  getProstatePostRpTStageOptions,
  getProstatePostRpTStages,
} from 'src/distribution_platforms/features/order_form';
import { SurgicalMargin, TStage } from 'src/types';
import { SurgicalMarginUtils } from 'src/utils';

const SURGICAL_MARGIN_OPTIONS = [
  {
    value: SurgicalMargin.Positive,
    name: SurgicalMarginUtils.getSurgicalMarginName(SurgicalMargin.Positive),
  },
  {
    value: SurgicalMargin.Negative,
    name: SurgicalMarginUtils.getSurgicalMarginName(SurgicalMargin.Negative),
  },
];

export const createLdtPostRpTStageAndSurgicalMarginFieldsSchema = (
  includeTStageNotProvided = false,
  allowTStageNotProvided = false,
) =>
  z.object({
    tStage: z
      .enum(getProstatePostRpTStages(includeTStageNotProvided), {
        error: 'T-Stage is required',
      })
      .superRefine((val, ctx) => {
        if (!allowTStageNotProvided && val === TStage.NotProvided) {
          ctx.addIssue({ code: 'custom', message: 'T-Stage is required' });
        }
      }),
    surgicalMargin: z.enum(Object.values(SurgicalMargin), {
      error: 'Surgical Margin is required',
    }),
  });

export type LdtPostRpTStageAndSurgicalMarginFieldsValues = z.infer<
  ReturnType<typeof createLdtPostRpTStageAndSurgicalMarginFieldsSchema>
>;

export const LdtPostRpTStageAndSurgicalMarginFields = <
  T extends LdtPostRpTStageAndSurgicalMarginFieldsValues,
>({
  control,
  errors,
  trigger,
  allowTStageNotProvided = false,
}: FormFieldProps<T> & { allowTStageNotProvided?: boolean }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'row' },
      gap: { xs: 2, sm: 3 },
    }}
  >
    <Box
      sx={{
        flex: 1,
      }}
    >
      <Controller
        name={'tStage' as Path<T>}
        control={control}
        render={({ field }) => (
          <RadioGroupField
            row
            required
            name={field.name}
            label='Pathologic T-stage'
            value={field.value}
            onBlur={field.onBlur}
            options={getProstatePostRpTStageOptions(allowTStageNotProvided)}
            validationText={errors.tStage?.message?.toString() ?? ''}
            onChange={async (event) => {
              field.onChange(event);
            }}
          />
        )}
      />
    </Box>
    <Box
      sx={{
        flex: 1,
      }}
    >
      <Controller
        name={'surgicalMargin' as Path<T>}
        control={control}
        render={({ field }) => (
          <RadioGroupField
            row
            required
            name={field.name}
            label='Surgical Margin'
            value={field.value}
            onBlur={field.onBlur}
            options={SURGICAL_MARGIN_OPTIONS}
            validationText={errors.surgicalMargin?.message?.toString() ?? ''}
            onChange={async (event) => {
              field.onChange(event);
              trigger?.('surgicalMargin' as Path<T>);
            }}
          />
        )}
      />
    </Box>
  </Box>
);
